/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React from 'react';

import { TIVO_SERVICES } from '../../../config';
import { ImageAssetsService } from '../../../core/infrastructure';
import { ServicesClientImpl } from '../../../core/interactors';
import { PreviewComponent, PreviewComponentAssets } from '../../../core/usecases';
import {
    Badge,
    ChannelAiringInfo,
    ContentDescription,
    Credits,
    Duration,
    FirstAirsOrAired,
    Genres,
    ParentalControlRating,
    SeaEpEpisodeTitle,
    Separator,
    ShowcaseBanner,
    StatusMessage,
    TimeAiringInfo,
} from '../ui-kit';
import { VodPreviewFormatter } from '../VodPreview/VodPreviewFormatter';
import { VodScreenType } from '../VodPreview/VodPreviewProps';
import type { TInfoCardContentUtil } from './InfoCardProps';

/**
 * EpisodeInfoCard
 * Ref.: http://w3-engr.tivo.com/MyWorld/UserInterface/Hydra/Specs/STB/core_ux/components/components.html#episode_info_card
 * There is no line limit or truncation enforced for metadata display. Display all metadata
 * available for each part, for example, title, description, or actors list.
 * UX Ref. https://app.zeplin.io/project/6266f85e6716281480fdbbd8/screen/68c3da472426d6fbf0916e9a
 */
export const getEpisodeInfoCardContent: TInfoCardContentUtil = (metadata) => {
    const assetService = new ImageAssetsService({
        imageHostUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
    });

    const url: string = assetService.getEpisodeBannerImageUrlForVodContent(
        metadata.contentId || '',
    );

    const pcRatingNames = PreviewComponent.getParentalRatingNames(
        metadata?.internalRatingList || [],
    );
    const oPreviewAssets = new PreviewComponentAssets();
    const sContentDescription =
        metadata.description ||
        oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.DESCRIPTION_NOT_AVAILABLE,
        );
    const vodPreviewFormatter = new VodPreviewFormatter(metadata, VodScreenType.VOD_INFO_CARD);
    const entitlementAndAvailabilityStatus = vodPreviewFormatter.getCombinedStatusMessage();

    const getBodyContent = (): JSX.Element => {
        return (
            <React.Fragment>
                {!entitlementAndAvailabilityStatus && (
                    <>
                        {metadata.channel && (
                            <ChannelAiringInfo
                                channelNumber={metadata.channel.channelNumber}
                                callSign={metadata.channel.callSign}
                                videoResolution={
                                    metadata.channel.videoResolution || metadata.videoResolution
                                }
                                isThreeD={metadata.isThreeD}
                            />
                        )}
                        {metadata.channel && metadata.startTime && metadata.endTime && (
                            <Separator />
                        )}
                        {metadata.startTime && metadata.endTime && (
                            <TimeAiringInfo
                                startTime={metadata.startTime}
                                endTime={metadata.endTime}
                            />
                        )}
                    </>
                )}
                <StatusMessage
                    entitlementAndAvailabilityStatus={entitlementAndAvailabilityStatus}
                    status={metadata.recordingStatus}
                    isKeepForeverRecording={metadata.isKeepForeverRecording}
                    showType={metadata.previewType}
                    isPartialRecording={metadata.isPartialRecording}
                    startTime={metadata.startTime}
                    endTime={metadata.endTime}
                    duration={metadata.recordingDuration}
                    recordingAvailability={metadata.recordingAvailability}
                />
                <SeaEpEpisodeTitle
                    seriesTitle={metadata.title}
                    episodeTitle={metadata.episodeTitle}
                    episodeNumber={metadata.episodeNumber}
                    seasonNumber={metadata.seasonNumber}
                    quoted={false}
                    trailingSeaEp={true}
                />
                {entitlementAndAvailabilityStatus && (
                    <Badge
                        text={metadata.videoResolution || metadata.channel?.videoResolution}
                        uppercase={true}
                    />
                )}
                <Badge text={metadata.isNew ? 'New' : ''} />
                <ParentalControlRating ratingNames={pcRatingNames} />
                {entitlementAndAvailabilityStatus &&
                    metadata.videoResolution &&
                    metadata.isNew &&
                    pcRatingNames.length > 0 && <Separator />}
                <Genres categoryLabel={metadata.categoryLabel} />
                <Separator />
                <Duration duration={metadata.duration} />
                <Separator />
                <FirstAirsOrAired firstAiredDate={metadata.firstAiredDate} />
                <Credits creditString={metadata.creditString} />
                <ContentDescription description={sContentDescription} />
            </React.Fragment>
        );
    };

    return {
        headerContent: (
            <ShowcaseBanner
                bannerUrl={url || metadata.episodePreviewImageUrl || ''}
                fallbackText={metadata.title || metadata.episodeTitle || ''}
            />
        ),
        headerClassName: 'banner-only',
        bodyContent: getBodyContent(),
    };
};
